import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Phone, 
  PhoneIncoming,
  Clock, 
  User, 
  FileText, 
  AlertCircle,
  CheckCircle,
  Video,
  Timer,
  Volume2,
  Loader2,
  Key,
  XCircle
} from 'lucide-react';
import { BrandingProvider, useBranding } from '../../contexts/BrandingContext';
import { 
  BrandedHeader, 
  BrandedButton, 
  BrandedCard, 
  BrandedBadge 
} from '../../components/branding/BrandedComponents';
import { useAuth } from '../../contexts/AuthContext';

// Real consultation API integration
interface PendingConsultation {
  requestId: string;
  clientName: string;
  firmName: string;
  caseType: string;
  urgency: 'high' | 'medium' | 'low';
  status: string;
  createdAt: string;
  expiresAt: string;
  waitTime: number; // in minutes
  metadata: {
    phone?: string;
    email?: string;
    notes?: string;
  };
}

interface AttorneyDashboardProps {
  attorneyName?: string;
  firmName?: string;
  onAcceptClient?: (clientId: string) => void;
  onRejectClient?: (clientId: string) => void;
}

export const AttorneyDashboard: React.FC<AttorneyDashboardProps> = ({
  attorneyName = 'Attorney',
  firmName = 'Smith & Associates',
  onAcceptClient,
  onRejectClient
}) => {
  return (
    <BrandingProvider templateType="legal" initialBranding={{ companyName: firmName }}>
      <AttorneyDashboardContent 
        attorneyName={attorneyName}
        firmName={firmName}
        onAcceptClient={onAcceptClient}
        onRejectClient={onRejectClient}
      />
    </BrandingProvider>
  );
};

const AttorneyDashboardContent: React.FC<AttorneyDashboardProps> = ({
  attorneyName = 'Attorney',
  firmName = 'Smith & Associates',
  onAcceptClient
}) => {
  const { getCurrentOrganizationId, isAuthenticated } = useAuth();
  const { branding } = useBranding();
  const [pendingConsultations, setPendingConsultations] = useState<PendingConsultation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAccepting, setIsAccepting] = useState<string | null>(null);
  const [isRejecting, setIsRejecting] = useState<string | null>(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  
  // Audio context for notification sounds
  const audioContextRef = useRef<AudioContext | null>(null);
  
  // Initialize audio context
  const initAudio = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    return audioContextRef.current;
  }, []);
  
  // Play notification sound
  const playNotificationSound = useCallback(() => {
    if (!soundEnabled) return;
    
    try {
      const audioContext = initAudio();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // High-pitched notification beep
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.1);
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.3);
      
      console.log('🔊 ATTORNEY: Notification sound played');
    } catch (error) {
      console.error('🔇 ATTORNEY: Failed to play sound:', error);
    }
  }, [soundEnabled, initAudio]);
  // Removed API key requirements - attorney dashboard works like other templates
  
  // Get organization ID for proper tenant isolation
  const orgId = getCurrentOrganizationId();
  
  // Removed API key save functionality - not needed anymore
  
  // Removed API key loading - attorney dashboard works without API keys now
  
  // Removed API key handling functions - not needed anymore
  
  // Log current state for debugging  
  console.log('🔐 ATTORNEY: Current organization ID:', orgId);
  console.log('🔑 ATTORNEY: Is authenticated:', isAuthenticated);
  
  // Fetch pending consultations from API
  const fetchPendingConsultations = useCallback(async (isInitial = false) => {
    if (!isAuthenticated || !orgId) {
      console.log('⚠️ ATTORNEY: Not authenticated or no org ID, skipping fetch');
      if (isInitial) {
        setIsInitialLoading(false);
      }
      return;
    }
    
    console.log(`🔍 ATTORNEY: Fetching pending consultations... (${isInitial ? 'initial' : 'refresh'})`);
    if (isInitial) {
      setIsInitialLoading(true);
    } else {
      setIsLoading(true);
    }
    setError(null);
    
    try {
      // Use organization-based endpoint like other templates (no API key required)
      console.log('🔍 ATTORNEY: Fetching consultations for organization:', orgId);
      const response = await fetch(`https://dev-platform-api-dev.benjiemalinao879557.workers.dev/client/v1/legal/consultations/pending?organizationId=${orgId}`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      console.log('📡 ATTORNEY: API Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('🔴 ATTORNEY: API Error response:', errorText);
        throw new Error(`Failed to fetch consultations: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('📊 ATTORNEY: Received consultation data:', data);
      
      const newConsultations = data.consultations || [];
      
      // Update consultations and log changes
      setPendingConsultations(prevConsultations => {
        if (prevConsultations.length !== newConsultations.length) {
          if (newConsultations.length > prevConsultations.length) {
            const newCount = newConsultations.length - prevConsultations.length;
            console.log('🔔 ATTORNEY: NEW CONSULTATION REQUESTS DETECTED!', {
              previousCount: prevConsultations.length,
              newCount: newConsultations.length,
              addedRequests: newCount
            });
            
            // Play notification sound for new consultations
            playNotificationSound();
          }
        }
        console.log('✅ ATTORNEY: Successfully updated consultation list. Total pending:', newConsultations.length);
        return newConsultations;
      });
      
    } catch (error) {
      console.error('🔴 ATTORNEY: Error fetching consultations:', error);
      setError('Failed to fetch pending consultations. Please try again later.');
    } finally {
      setIsLoading(false);
      if (isInitial) {
        setIsInitialLoading(false);
      }
    }
  }, [isAuthenticated, orgId]);

  // Accept consultation request
  const acceptConsultation = async (requestId: string) => {
    if (!isAuthenticated || !orgId) {
      setError('Not authenticated or no organization');
      return;
    }
    
    console.log('✅ ATTORNEY: Accepting consultation:', requestId);
    setIsAccepting(requestId);
    
    try {
      const response = await fetch(`https://dev-platform-api-dev.benjiemalinao879557.workers.dev/client/v1/legal/consultations/${requestId}/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          attorneyName: attorneyName,
          organizationId: orgId
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('🔴 ATTORNEY: Failed to accept consultation:', errorText);
        throw new Error(`Failed to accept consultation: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('✅ ATTORNEY: Successfully accepted consultation:', result);
      
      // Remove accepted consultation from list
      setPendingConsultations(prev => prev.filter(c => c.requestId !== requestId));
      
      // Navigate to video session if room info is provided
      if (result.roomId) {
        // Extract org ID from current URL if present
        const pathname = window.location.pathname;
        const orgMatch = pathname.match(/\/(org_[a-zA-Z0-9_]+)\//);
        const orgPrefix = orgMatch ? `/${orgMatch[1]}` : '';
        
        window.location.href = `${orgPrefix}/templates/legal/session/host?roomId=${result.roomId}&type=attorney`;
      }
      
      if (onAcceptClient) {
        onAcceptClient(requestId);
      }
      
    } catch (error) {
      console.error('🔴 ATTORNEY: Error accepting consultation:', error);
      setError('Failed to accept consultation. Please try again.');
    } finally {
      setIsAccepting(null);
    }
  };

  // Reject consultation request
  const rejectConsultation = async (requestId: string) => {
    if (!isAuthenticated || !orgId) {
      setError('Not authenticated or no organization');
      return;
    }
    
    console.log('❌ ATTORNEY: Rejecting consultation:', requestId);
    setIsRejecting(requestId);
    
    try {
      const response = await fetch(`https://dev-platform-api-dev.benjiemalinao879557.workers.dev/client/v1/legal/consultations/${requestId}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          attorneyName: attorneyName,
          organizationId: orgId,
          reason: 'Attorney unavailable'
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('🔴 ATTORNEY: Failed to reject consultation:', errorText);
        throw new Error(`Failed to reject consultation: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('❌ ATTORNEY: Successfully rejected consultation:', result);
      
      // Remove rejected consultation from list
      setPendingConsultations(prev => prev.filter(c => c.requestId !== requestId));
      
      // Optional: Show success message
      // You could add a toast notification here
      
    } catch (error) {
      console.error('🔴 ATTORNEY: Error rejecting consultation:', error);
      setError('Failed to reject consultation. Please try again.');
    } finally {
      setIsRejecting(null);
    }
  };

  // Fetch consultations on component mount and set up polling
  useEffect(() => {
    if (!isAuthenticated || !orgId) {
      return;
    }
    
    console.log('🚀 ATTORNEY: Attorney Dashboard mounted, starting consultation monitoring...');
    fetchPendingConsultations(true); // Initial load
    
    // Poll for new consultations every 30 seconds
    console.log('⏰ ATTORNEY: Setting up polling interval (30 seconds)...');
    const interval = setInterval(() => {
      console.log('🔄 ATTORNEY: Polling interval triggered - checking for new consultations...');
      fetchPendingConsultations(false); // Background refresh
    }, 30000);
    
    return () => {
      console.log('🛑 ATTORNEY: Cleaning up polling interval...');
      clearInterval(interval);
    };
  }, [isAuthenticated, orgId, fetchPendingConsultations]);

  // Check for authentication
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-red-50">
        <div className="text-center p-8 bg-white rounded-lg shadow-lg">
          <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-red-600 mb-2">Authentication Required</h2>
          <p className="text-gray-600 mb-4">You must be signed in to access the attorney dashboard.</p>
          <p className="text-sm text-gray-500">Please sign in to continue.</p>
        </div>
      </div>
    );
  }
  
  // Removed API key loading and input screens - attorney dashboard works without API keys
  
  const getUrgencyBadge = (urgency: string) => {
    switch (urgency) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (isInitialLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading pending consultations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 mb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <BrandedHeader 
            showLogo={true}
            showTagline={true}
            size="medium"
          />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mt-4">Attorney Dashboard</h1>
          <p className="text-gray-600 dark:text-gray-400">{branding.companyName || firmName} - {attorneyName}</p>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <BrandedCard className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Pending Consultations</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">{pendingConsultations.length}</p>
              </div>
              <PhoneIncoming className="w-8 h-8 text-primary" />
            </div>
          </BrandedCard>
          
          <BrandedCard className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">High Priority</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                  {pendingConsultations.filter(c => c.urgency === 'high').length}
                </p>
              </div>
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
          </BrandedCard>
          
          <BrandedCard className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Avg Wait Time</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                  {pendingConsultations.length > 0 
                    ? Math.round(pendingConsultations.reduce((acc, c) => acc + c.waitTime, 0) / pendingConsultations.length) 
                    : 0} min
                </p>
              </div>
              <Clock className="w-8 h-8 text-yellow-500" />
            </div>
          </BrandedCard>
          
          <BrandedCard className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Status</p>
                <p className="text-lg font-semibold text-green-600 dark:text-green-400">Available</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </BrandedCard>
        </div>

        {/* Sound Toggle */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-3">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Pending Consultations</h2>
            {/* Background loading indicator */}
            {isLoading && (
              <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 dark:border-gray-600 border-t-primary"></div>
                <span>Checking for updates...</span>
              </div>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => setSoundEnabled(!soundEnabled)}
              className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 shadow-sm dark:shadow-gray-900/20 transition-all duration-200"
            >
              <Volume2 className={`w-4 h-4 transition-colors ${soundEnabled ? 'text-primary' : 'text-gray-400 dark:text-gray-500'}`} />
              <span className="text-sm text-gray-700 dark:text-gray-200 font-medium">
                {soundEnabled ? 'Sound On' : 'Sound Off'}
              </span>
            </button>
            
            <button
              type="button"
              onClick={playNotificationSound}
              className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-primary hover:bg-primary/90 text-white shadow-sm transition-all duration-200"
            >
              <span className="text-sm font-medium">Test Sound</span>
            </button>
          </div>
        </div>

        {/* Consultations List */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg backdrop-blur-sm">
            <div className="flex items-center space-x-2">
              <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
              <p className="text-red-600 dark:text-red-300 font-medium">{error}</p>
            </div>
          </div>
        )}
        
        <div className="space-y-4">
          {pendingConsultations.length === 0 ? (
            <BrandedCard className="p-12 text-center">
              <Phone className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Pending Consultations</h3>
              <p className="text-gray-500 dark:text-gray-400">New consultation requests will appear here.</p>
            </BrandedCard>
          ) : (
            pendingConsultations.map((consultation, index) => (
              <BrandedCard key={consultation.requestId} className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-4 mb-3">
                      <span className="text-lg font-semibold text-gray-900 dark:text-white">
                        {index + 1}. {consultation.clientName}
                      </span>
                      <BrandedBadge className={getUrgencyBadge(consultation.urgency)}>
                        {consultation.urgency.toUpperCase()}
                      </BrandedBadge>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div className="flex items-center space-x-2">
                        <FileText className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                        <span className="text-sm text-gray-600 dark:text-gray-300 font-medium">
                          {consultation.caseType}
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <User className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                        <span className="text-sm text-gray-600 dark:text-gray-300 font-medium">
                          {consultation.firmName}
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Timer className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                        <span className="text-sm text-gray-600 dark:text-gray-300 font-medium">
                          {consultation.waitTime} min wait
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-600">•</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          ID: {consultation.requestId.split('_').pop()}
                        </span>
                      </div>
                    </div>
                    
                    {consultation.metadata.notes && (
                      <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-800/50 border dark:border-gray-700/50 rounded-lg">
                        <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                          {consultation.metadata.notes}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  <div className="ml-4 flex flex-col space-y-2">
                    <BrandedButton
                      onClick={() => acceptConsultation(consultation.requestId)}
                      disabled={isAccepting === consultation.requestId || isRejecting === consultation.requestId}
                      className="flex items-center space-x-2"
                    >
                      {isAccepting === consultation.requestId ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>Connecting...</span>
                        </>
                      ) : (
                        <>
                          <Video className="w-4 h-4" />
                          <span>Accept Consultation</span>
                        </>
                      )}
                    </BrandedButton>
                    
                    <BrandedButton
                      onClick={() => rejectConsultation(consultation.requestId)}
                      disabled={isRejecting === consultation.requestId || isAccepting === consultation.requestId}
                      variant="outline"
                      className="flex items-center space-x-2"
                    >
                      {isRejecting === consultation.requestId ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                          <span>Rejecting...</span>
                        </>
                      ) : (
                        <>
                          <XCircle className="w-4 h-4" />
                          <span>Reject Request</span>
                        </>
                      )}
                    </BrandedButton>
                  </div>
                </div>
              </BrandedCard>
            ))
          )}
        </div>
        
        {/* Auto-refresh indicator */}
        <div className="mt-8 text-center text-sm text-gray-500 dark:text-gray-400">
          <div className="flex items-center justify-center space-x-2 bg-white dark:bg-gray-800/50 rounded-full px-4 py-2 border border-gray-200 dark:border-gray-700/50 shadow-sm dark:shadow-gray-900/10 backdrop-blur-sm">
            <div className="animate-pulse w-2 h-2 bg-green-500 dark:bg-green-400 rounded-full"></div>
            <span className="text-gray-600 dark:text-gray-300 font-medium">Auto-refreshing every 30 seconds</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttorneyDashboard;